import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { fetcherAuth } from "utils/api";

interface PastUpload {
  filename: string;
  url: string;
  date: string | null;
}

interface PastUploadsDialogProps {
  open: boolean;
  handleClose: () => void;
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return dateStr;
  }
};

export default function PastUploadsDialog({
  open,
  handleClose,
}: PastUploadsDialogProps) {
  const { getToken } = useAuth();
  const [uploads, setUploads] = useState<PastUpload[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    if (open) {
      fetchUploads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchUploads = async () => {
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        "/api/admin/google-place-brands/uploads",
        "GET",
      );
      setUploads(res.uploads || []);
    } catch {
      // non-critical
    }
    setLoading(false);
  };

  const sortedUploads = useMemo(() => {
    return [...uploads].sort((a, b) => {
      const dateA = a.date || "";
      const dateB = b.date || "";
      return sortDirection === "asc"
        ? dateA.localeCompare(dateB)
        : dateB.localeCompare(dateA);
    });
  }, [uploads, sortDirection]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Past Uploads</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : uploads.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No past uploads found.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active
                      direction={sortDirection}
                      onClick={() =>
                        setSortDirection((prev) =>
                          prev === "asc" ? "desc" : "asc",
                        )
                      }
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>File</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedUploads.map((upload) => (
                  <TableRow key={upload.filename} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography variant="body2" fontWeight={600}>
                        {formatDate(upload.date)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 400, overflow: "hidden" }}>
                      <Link
                        href={upload.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        sx={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {upload.filename}
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
